import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../apis";
import type { PaginationProps } from "antd";
import { Pagination } from "antd";
import Empty from "../elements/empty";
import CardLoader from "../elements/card-loader";
import AppointmentCard from "../card/appointment-card";
import Searchbar from "../elements/searchbar";

export default function AppointmentList(_props?: unknown) {
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(9);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  // Debounced so typing doesn't fire a request per keystroke — same pattern
  // the medicine/insurance search fields already use elsewhere in this app.
  // `fetchData` depends on `debouncedSearch`, not raw `searchText`, so the
  // fetch itself is what's actually delayed, not just a side effect next to it.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset to page 1 whenever the (debounced) search term changes — a search
  // fired while sitting on page 3 would otherwise silently query page 3 of
  // results that may not exist for the new term.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await DataProvider.getAllAppointment(
        currentPage,
        pageSize,
        debouncedSearch || undefined
      );
      setData(results.data.appointments);
      setTotalPages(results.data.totalPages);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onChange: PaginationProps["onChange"] = (page) => {
    setCurrentPage(page);
    setPageSize(9);
  };

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200/80 shadow-card overflow-hidden"
      aria-label="All appointments"
    >
      <div className="border-b border-slate-200/80 bg-slate-50/50 px-5 py-4">
        <h2 className="text-sm font-semibold text-slate-800 tracking-tight">
          All appointments
        </h2>
        <div className="mt-3 w-full max-w-md">
          <Searchbar searchText={searchText} setSearchText={setSearchText} />
        </div>
      </div>
      <div className="p-5">
        {loading && data === null ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((v) => (
              <CardLoader key={v} />
            ))}
          </div>
        ) : data && data.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.map((v, idx) => (
                <AppointmentCard data={v} key={v.id ?? idx} />
              ))}
            </div>
            <div className="mt-6 flex justify-center">
              <Pagination
                current={currentPage}
                onChange={onChange}
                total={totalPages}
                showSizeChanger={false}
                className="ant-pagination-modern"
              />
            </div>
          </>
        ) : (
          <Empty
            imageStyle="md:h-44 h-24"
            titleStyle="md:text-lg text-sm"
            desStyle="md:text-base"
            title="No appointments"
            buttons={[{ name: "Refresh", event: () => fetchData() }]}
            description="No appointments available."
          />
        )}
      </div>
    </section>
  );
}
